/**
 * AuthCallback — where Supabase sends the browser back after a magic link,
 * a signup confirmation, or Google / Microsoft sign-in.
 *
 * Flow: Authorization Code + PKCE (see lib/supabase.ts). The SDK exchanges the
 * one-time `?code=` in this URL for a session while the client initialises
 * (`detectSessionInUrl`), using the code verifier it stored when the flow
 * started. This page only waits for that session, then navigates.
 *
 * The verifier lives in the browser that STARTED the flow, so a link opened on
 * a different device or browser cannot complete. We say exactly that instead
 * of surfacing the SDK's generic error, and offer a fresh link.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

type Status = 'working' | 'error';

export function AuthCallback() {
  useEffect(() => { document.title = 'Signing you in — Waypoint'; }, []);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<Status>('working');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function handleCallback() {
      // 1) Surface any Supabase auth error returned in the URL.
      const errorParam = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');
      if (errorParam) {
        if (!cancelled) {
          setStatus('error');
          setErrorMessage(decodeURIComponent(errorDescription ?? errorParam));
        }
        return;
      }

      // 2) The SDK exchanges `?code=` for a session on init (PKCE). Wait for it —
      // the exchange is a network round-trip, so poll briefly rather than
      // calling exchangeCodeForSession() a second time (a code is single-use).
      for (let attempt = 0; attempt < 60; attempt++) {
        const { data } = await supabase.auth.getSession();
        if (cancelled) return;
        if (data.session) {
          const next = searchParams.get('next');
          navigate(next ? decodeURIComponent(next) : '/dashboard', { replace: true });
          return;
        }
        // No session yet — wait 100ms and retry. Total budget: 6 seconds.
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      if (!cancelled) {
        setStatus('error');
        setErrorMessage(
          searchParams.get('code')
            ? 'This link can only finish signing you in from the browser and device where you requested it. Open it there, or request a new link here.'
            : 'Your sign-in link could not be verified. It may have expired or already been used — request a new one.'
        );
      }
    }

    handleCallback();

    return () => {
      cancelled = true;
    };
  }, [navigate, searchParams]);

  if (status === 'error') {
    return (
      <div className="flex min-h-screen items-center justify-center px-6 py-12">
        <div className="w-full max-w-md rounded-xl border border-danger/30 bg-surface-1 p-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-danger-soft text-2xl">
            !
          </div>
          <h1 className="mb-2 text-xl font-medium text-ink">Sign-in failed</h1>
          <p className="mb-6 text-sm leading-relaxed text-ink-muted">{errorMessage}</p>
          <Link to="/login" className="btn btn-primary">
            Request a new link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen w-screen items-center justify-center">
      <div className="font-mono text-xs uppercase tracking-widest text-ink-muted">
        <span className="mr-2 inline-block h-2 w-2 animate-pulse rounded-full bg-primary-bright" />
        Signing you in…
      </div>
    </div>
  );
}
